"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PropertyScore = void 0;
const tslib_1 = require("tslib");
const deepmerge_1 = tslib_1.__importDefault(require("deepmerge"));
class PropertyScore {
    constructor({ metaKey = "score", scoreKey = "score", defaultValue = 0, detailedScore = false, calculateOnUpdate = true, }) {
        this.selectorsScore = {};
        this.defaultScoreData = {
            current: 0,
            max: 0,
            maxQualified: 0,
            detailsByQuestion: {},
            detailsBySection: {},
        };
        this.metaKey = metaKey;
        this.defaultValue = defaultValue;
        this.detailedScore = detailedScore;
        this.scoreKey = scoreKey;
        this.scoreByValueKey = `${this.scoreKey}ByValue`;
        this.calculateOnUpdate = calculateOnUpdate;
    }
    formCreated({ form }) {
        const scoreData = (0, deepmerge_1.default)({}, this.defaultScoreData);
        for (const selector of Object.keys(form.validations)) {
            this.initializePropertyScore(selector, form, scoreData);
        }
        for (const selector of Object.keys(this.selectorsScore)) {
            let selMaxScore = 0;
            const selectorScore = this.selectorsScore[selector].scoring;
            if (typeof selectorScore === "object") {
                selMaxScore = this.defaultValue;
                for (const score of Object.values(selectorScore)) {
                    if (score > selMaxScore) {
                        selMaxScore = score;
                    }
                }
            }
            else {
                selMaxScore = selectorScore;
            }
            scoreData.max += selMaxScore;
            if (this.detailedScore && scoreData.detailsByQuestion[selector]) {
                scoreData.detailsByQuestion[selector].max = selMaxScore;
            }
        }
        if (this.detailedScore) {
            form.schema.nodes.sections.forEach((section) => {
                for (const childSelector of section.answerableSelectors) {
                    scoreData.detailsBySection[section.property.UID].max += this.selectorsScore[childSelector].max;
                }
            });
        }
        form.meta[this.metaKey] = scoreData;
        this.calculate({ form });
    }
    dataUpdated(eventCtx) {
        if (this.calculateOnUpdate) {
            this.calculate(eventCtx);
        }
    }
    formPass(eventCtx) {
        if (!this.calculateOnUpdate) {
            this.calculate(eventCtx);
        }
    }
    calculate({ form }) {
        var _a;
        let total = 0;
        const meta = form.meta[this.metaKey];
        meta.maxQualified = 0;
        if (this.detailedScore) {
            for (const selector of Object.keys(meta.detailsBySection)) {
                meta.detailsBySection[selector] = Object.assign(Object.assign({}, meta.detailsBySection[selector]), { current: 0, maxQualified: 0 });
            }
        }
        for (const prop of form.selectors.values) {
            if (!prop.isAnswerable) {
                continue;
            }
            if (prop.belongToGroup && !form.nodes.groups.isValid(prop.groupUID)) {
                continue;
            }
            if (form.qualifications[prop.UID] && form.validations[prop.UID]) {
                const value = this.propCurrentScoreValue(form, prop.UID);
                total += value;
                if (this.detailedScore && meta.detailsByQuestion[prop.UID]) {
                    meta.detailsByQuestion[prop.UID].current = value;
                    this.updateParentSectionCurrentValue(form, prop.UID, value);
                }
            }
            else if (this.detailedScore && meta.detailsByQuestion[prop.UID]) {
                meta.detailsByQuestion[prop.UID].current = 0;
            }
            if (form.qualifications[prop.UID]) {
                meta.maxQualified += this.selectorsScore[prop.UID].max;
            }
        }
        if (this.detailedScore) {
            for (const sectionSelector of Object.keys(meta.detailsBySection)) {
                const childrenSelectors = (_a = form.schema.nodes.sections.get(sectionSelector)) === null || _a === void 0 ? void 0 : _a.answerableSelectors;
                let maxQualified = 0;
                for (const propSel of childrenSelectors) {
                    if (form.qualifications[propSel]) {
                        maxQualified += meta.detailsByQuestion[propSel].max;
                    }
                }
                meta.detailsBySection[sectionSelector].maxQualified = maxQualified;
            }
        }
        meta.current = total;
    }
    updateParentSectionCurrentValue(form, selector, value) {
        const prop = form.selectors.selectors[selector];
        if (prop.isSection) {
            form.meta[this.metaKey].detailsBySection[selector].current += value;
        }
        if (prop.parentUID) {
            this.updateParentSectionCurrentValue(form, prop.parentUID, value);
        }
    }
    propCurrentScoreValue(form, selector) {
        let value = this.defaultValue;
        const selectorScoring = this.selectorsScore[selector].scoring;
        if (typeof selectorScoring === "object") {
            const [, data] = form.dataSelector.tryGet(selector);
            if (selectorScoring[data]) {
                value = selectorScoring[data];
            }
        }
        else {
            value = selectorScoring;
        }
        return value;
    }
    propDefToSelectorScore(prop) {
        if (prop.def[this.scoreKey] !== undefined) {
            return {
                max: prop.def[this.scoreKey],
                scoring: prop.def[this.scoreKey],
            };
        }
        else if (prop.def[this.scoreByValueKey]) {
            let max = this.defaultValue;
            for (const v of Object.values(prop.def[this.scoreByValueKey])) {
                const value = v;
                if (value > max) {
                    max = value;
                }
            }
            return {
                max: max,
                scoring: prop.def[this.scoreByValueKey],
            };
        }
        else if (prop.def["enum"]) {
            const selectorScore = {
                max: 0,
                scoring: {},
            };
            for (const el of prop.def["enum"]) {
                const elType = typeof el;
                if (elType === "object" && el[this.scoreKey] !== undefined && el["value"] !== undefined) {
                    selectorScore.scoring[el["value"]] = el[this.scoreKey];
                    if (el[this.scoreKey] > selectorScore.max) {
                        selectorScore.max = el[this.scoreKey];
                    }
                }
                else if (elType !== "object" || el[this.scoreKey] === undefined) {
                    let keyValue = null;
                    if (elType === "object" && el["value"] !== undefined) {
                        keyValue = el["value"];
                    }
                    else if (elType !== "object") {
                        keyValue = el;
                    }
                    if (keyValue !== null) {
                        selectorScore.scoring[keyValue] = this.defaultValue;
                    }
                    if (this.defaultValue > selectorScore.max) {
                        selectorScore.max = this.defaultValue;
                    }
                }
            }
            return selectorScore;
        }
        return {
            max: this.defaultValue,
            scoring: this.defaultValue,
        };
    }
    initializePropertyScore(selector, form, scoreData) {
        const prop = form.schema.search(selector);
        if (!prop) {
            return;
        }
        if (this.detailedScore) {
            if (prop.isSection()) {
                scoreData.detailsBySection[selector] = {
                    current: 0,
                    max: 0,
                    maxQualified: 0,
                };
            }
            else if (prop.isAnswerable()) {
                scoreData.detailsByQuestion[selector] = {
                    current: 0,
                    max: 0,
                };
            }
        }
        if (!prop.isSection() && !prop.isGroup()) {
            this.selectorsScore[selector] = this.propDefToSelectorScore(prop);
        }
    }
    addSelector(selector, form) {
        this.initializePropertyScore(selector, form, form.meta[this.scoreKey]);
    }
    removeSelector(selector, form) {
        delete form.meta[this.scoreKey].detailsByQuestion[selector];
        delete form.meta[this.scoreKey].detailsBySection[selector];
        delete this.selectorsScore[selector];
    }
}
exports.PropertyScore = PropertyScore;
